// Per-user, per-competition cache for "is the current user on a team for this
// competition" — minimal shape (just the team id) so any page that knows the
// answer can populate it without constructing a full MyTeamPayload.
//
// Why minimal: the workspace only needs `hasJoinedTeam` (truthy/falsy) and
// the team id (for the Team-tab navigation). The full MyTeamPayload is
// fetched on mount as the source of truth; the cache only exists to seed the
// FIRST FRAME so we don't flash the "Find Team" label.
//
// Keying: \`coravu.myTeam:{userId}:{competitionId}\`. Per-user prefix so
// shared-browser sign-ins don't clobber each other; we never wipe on logout.

const PREFIX = "coravu.myTeam:";

export type CachedMyTeam = { teamId: string } | null;

function key(userId: string, competitionId: string): string {
  return `${PREFIX}${userId}:${competitionId}`;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Read the cached value:
 *   { teamId } → user is on this team
 *   null      → confirmed not on a team for this comp
 *   undefined → no cached value yet (never visited)
 */
export function readCachedMyTeam(
  userId: string,
  competitionId: string
): CachedMyTeam | undefined {
  const s = storage();
  if (!s) return undefined;
  try {
    const raw = s.getItem(key(userId, competitionId));
    if (raw === null) return undefined;
    if (raw === "null") return null;
    const parsed = JSON.parse(raw) as { teamId?: unknown };
    if (parsed && typeof parsed.teamId === "string" && parsed.teamId) {
      return { teamId: parsed.teamId };
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Persist the latest team id (or null = "not on a team"). */
export function writeCachedMyTeam(
  userId: string,
  competitionId: string,
  payload: CachedMyTeam
): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(
      key(userId, competitionId),
      payload === null ? "null" : JSON.stringify({ teamId: payload.teamId })
    );
  } catch {
    // quota / private mode — ignore
  }
}

/** Drop the cached entry. Use after leaveTeam / kicked / team deleted. */
export function clearCachedMyTeam(userId: string, competitionId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(userId, competitionId));
  } catch {
    // ignore
  }
}
